import { useState, lazy, Suspense } from "react";
import PokemonFilter from "@/components/PokemonFilter";
import LoadingAnimation from "@/components/LoadingAnimation";
import { useLanguage } from "@/services/languageService";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";
import StaticContent from "../components/StaticContent";
import { Pokemon, PokemonFilters, fetchRandomPokemon } from "@/services/pokeApi";
import { toast } from "sonner";
import MainLayout from "@/layouts/MainLayout";
import WhichPokemonBanner from "@/components/WhichPokemonBanner";

const PokemonGrid = lazy(() => import("@/components/PokemonGrid"));

const getFilterSignature = (filters: PokemonFilters) =>
  JSON.stringify({
    type: filters.type && filters.type !== "any" ? filters.type : "",
    generation: filters.generation && filters.generation !== "any" ? filters.generation : "",
    rarity: filters.rarity && filters.rarity !== "any" ? filters.rarity : "",
    evolutionStage:
      filters.evolutionStage && filters.evolutionStage !== "all" ? filters.evolutionStage : "all",
    gender: filters.gender ?? "",
    ability: filters.ability ?? "",
  });

const mergeLockedPokemon = (
  currentPokemon: Pokemon[],
  lockedIds: Set<number>,
  count: number,
  replacements: Pokemon[]
) => {
  const nextPokemon: Pokemon[] = [];
  let replacementIndex = 0;

  for (let slotIndex = 0; slotIndex < count; slotIndex += 1) {
    const currentSlotPokemon = currentPokemon[slotIndex];

    if (currentSlotPokemon && lockedIds.has(currentSlotPokemon.id)) {
      nextPokemon.push(currentSlotPokemon);
      continue;
    }

    const replacementPokemon = replacements[replacementIndex];
    if (replacementPokemon) {
      nextPokemon.push(replacementPokemon);
      replacementIndex += 1;
    }
  }

  return nextPokemon;
};

const Index = () => {
  const { t, currentLanguage } = useLanguage();
  const [pokemon, setPokemon] = useState<Pokemon[]>([]);
  const [loading, setLoading] = useState(false);
  const [isContentExpanded, setIsContentExpanded] = useState(false);
  const [lockedPokemonIds, setLockedPokemonIds] = useState<number[]>([]);
  const [lastFilterSignature, setLastFilterSignature] = useState<string>("");

  const handleToggleLock = (pokemonId: number) => {
    setLockedPokemonIds((currentLockedPokemonIds) =>
      currentLockedPokemonIds.includes(pokemonId)
        ? currentLockedPokemonIds.filter((id) => id !== pokemonId)
        : [...currentLockedPokemonIds, pokemonId]
    );
  };

  const handleGenerate = async (count: number, filters: PokemonFilters) => {
    setLoading(true);
    try {
      const nextFilterSignature = getFilterSignature(filters);
      const canReuseLockedPokemon = pokemon.length > 0 && lastFilterSignature === nextFilterSignature;
      const visibleLockedPokemonIds = canReuseLockedPokemon
        ? lockedPokemonIds.filter((lockedId) =>
            pokemon.slice(0, count).some((entry) => entry.id === lockedId)
          )
        : [];
      const lockedPokemonIdSet = new Set(visibleLockedPokemonIds);
      const lockedPokemonCount = pokemon
        .slice(0, count)
        .filter((entry) => lockedPokemonIdSet.has(entry.id)).length;

      const fetchedPokemon =
        lockedPokemonCount < count
          ? await fetchRandomPokemon(count - lockedPokemonCount, filters, {
              excludeIds: Array.from(lockedPokemonIdSet),
            })
          : [];
      const nextPokemon = canReuseLockedPokemon
        ? mergeLockedPokemon(pokemon, lockedPokemonIdSet, count, fetchedPokemon)
        : [...fetchedPokemon];
      const nextLockedPokemonIds = nextPokemon
        .filter((entry) => lockedPokemonIdSet.has(entry.id))
        .map((entry) => entry.id);

      setPokemon([...nextPokemon]);
      setLockedPokemonIds(nextLockedPokemonIds);
      setLastFilterSignature(nextFilterSignature);

      if (nextPokemon.length > 0 && (fetchedPokemon.length > 0 || nextLockedPokemonIds.length === 0)) {
        toast.success(`Generated ${nextPokemon.length} Pok\u00e9mon!`);
      }

      console.debug(
        `Current language: ${currentLanguage}, rendered Pok\u00e9mon count: ${nextPokemon.length}`
      );
      nextPokemon.forEach((entry) => {
        console.debug(
          `Pok\u00e9mon ID: ${entry.id}, name: ${entry.name}, localized names loaded: ${
            entry.localizedNames ? "yes" : "no"
          }`
        );
      });
    } catch (error) {
      console.error("Failed to generate Pok\u00e9mon:", error);
      toast.error("Failed to generate Pok\u00e9mon. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 py-8 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-pokemon-blue mb-2">{t("title")}</h1>
            <p className="text-gray-600">{t("subtitle")}</p>
          </div>

          <div className="mb-8">
            <PokemonFilter onGenerate={handleGenerate} />
          </div>

          {loading ? (
            <LoadingAnimation className="my-12" />
          ) : (
            <Suspense fallback={<LoadingAnimation className="my-12" />}>
              <PokemonGrid
                pokemon={pokemon}
                lockedPokemonIds={lockedPokemonIds}
                onToggleLock={handleToggleLock}
              />
            </Suspense>
          )}

          <WhichPokemonBanner />

          <Suspense fallback={<div>Loading content...</div>}>
            <div className="mt-16 mb-12 bg-white rounded-lg shadow-md p-6">
              <Collapsible
                open={isContentExpanded}
                onOpenChange={setIsContentExpanded}
                className="w-full"
              >
                <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-left font-semibold text-lg text-pokemon-blue">
                  <h2>{t("aboutTitle")}</h2>
                  {isContentExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-4 text-gray-700 space-y-8">
                  <section id="introduction">
                    <h2 className="text-2xl font-bold text-pokemon-blue mb-3">{t("introTitle")}</h2>
                    <StaticContent section="introduction" />
                  </section>

                  <section id="how-to-use">
                    <h2 className="text-2xl font-bold text-pokemon-blue mb-3">{t("howToUseTitle")}</h2>
                    <StaticContent section="howToUse" />
                  </section>

                  <section id="benefits">
                    <h2 className="text-2xl font-bold text-pokemon-blue mb-3">{t("benefitsTitle")}</h2>
                    <StaticContent section="benefits" />
                  </section>

                  <section id="history">
                    <h2 className="text-2xl font-bold text-pokemon-blue mb-3">{t("historyTitle")}</h2>
                    <StaticContent section="history" />
                  </section>

                  <section id="popular-pokemon">
                    <h2 className="text-2xl font-bold text-pokemon-blue mb-3">
                      {t("popularPokemonTitle")}
                    </h2>
                    <StaticContent section="popularPokemon" />
                  </section>

                  <section id="popular-games">
                    <h2 className="text-2xl font-bold text-pokemon-blue mb-3">{t("popularGamesTitle")}</h2>
                    <StaticContent section="popularGames" />
                  </section>

                  <section id="trading-contests">
                    <h2 className="text-2xl font-bold text-pokemon-blue mb-3">
                      {t("tradingContestsTitle")}
                    </h2>
                    <StaticContent section="tradingContests" />
                  </section>

                  <section id="faq">
                    <h2 className="text-2xl font-bold text-pokemon-blue mb-3">{t("faqTitle")}</h2>
                    <StaticContent section="faq" />
                  </section>

                  <section id="conclusion">
                    <h2 className="text-2xl font-bold text-pokemon-blue mb-3">{t("conclusionTitle")}</h2>
                    <StaticContent section="conclusion" />
                  </section>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </Suspense>
        </div>
      </div>
    </MainLayout>
  );
};

export default Index;
